<script setup lang="ts">
// -------------------------------
// 🔹 Imports Vue & composables
// -------------------------------
import { ref } from 'vue';

// -------------------------------
// 🔹 Composants
// -------------------------------

import BaseProduct from '@/components/common/BaseProduct.vue';
import BaseButtonPrimary from '@/components/common/BaseButtonPrimary.vue';

// -------------------------------
// 🔹 Stores
// -------------------------------
import { useDialogStore } from '@/store/dialog.store';

// -------------------------------
// 🔹 Utils & refs
// -------------------------------
import { getDateFormat, getDateHourMinute } from '@/utils/formatDate';
import { formatPrice } from '@/utils/formatPrice';
import { isNextStep_dialog } from '@/refs/dashboard.refs';
import BaseNotDataFound from '../common/BaseNotDataFound.vue';

// -------------------------------
// 🔹 Stores instanciés
// -------------------------------
const dialogStore = useDialogStore();
// -------------------------------
// 🔹 Gestion du dialogue
// -------------------------------

/**
 * Passe à l’étape suivante dans le dialogue (stepper).
 */
const handleNextStep = (): void => {
  isNextStep_dialog.value = !isNextStep_dialog.value;
};

/**
 * Ferme le dialogue et réinitialise les valeurs associées.
 */
const handleClose = (): void => {
  dialogStore.closeDialog();
};
</script>

<template>
  <div>
    <div>
      <div class="w-full space-y-2" v-if="!isNextStep_dialog">
        <div class="flex w-full flex-col justify-between gap-1">
          <div>
            <p
              class="text-grand-text text-14px font-tt-semiBold-Normal leading-normal"
            >
              Information du Suplémentaire
            </p>
          </div>

          <div
            class="flex w-full flex-row items-center justify-between"
            v-show="dialogStore.content.items?.code"
          >
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Code de promotion
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content.items?.code }}
            </p>
          </div>
          <div
            class="flex w-full flex-row items-center justify-between"
            v-show="dialogStore.content.items?.minimumOrderAmount"
          >
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Montant de commande minimum
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content.items?.minimumOrderAmount }}
            </p>
          </div>

          <div
            class="flex w-full flex-row items-center justify-between"
            v-show="dialogStore.content.items?.maximumDiscountAmount"
          >
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Montant de réduction maximum
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content.items?.maximumDiscountAmount }}
            </p>
          </div>
          <div
            class="flex w-full flex-row items-center justify-between"
            v-show="dialogStore.content.items?.startDate"
          >
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Date de début de la promotion
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ getDateFormat(dialogStore.content.items?.startDate) }} à
              {{ getDateHourMinute(dialogStore.content.items?.endDate) }}
            </p>
          </div>
          <div
            class="flex w-full flex-row items-center justify-between"
            v-show="dialogStore.content.items?.endDate"
          >
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Date de fin de la promotion
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ getDateFormat(dialogStore.content.items?.endDate) }} à
              {{ getDateHourMinute(dialogStore.content.items?.endDate) }}
            </p>
          </div>
          <div class="flex w-full flex-row items-center justify-between">
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
              v-show="dialogStore.content.items?.usageLimit"
            >
              limite d'utilisation
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content.items?.usageLimit }}
            </p>
          </div>

          <div
            class="flex w-full flex-row items-center justify-between"
            v-show="dialogStore.content.items?.usageCount"
          >
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Nombre de fois utilisé par compte
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content.items?.usageCount }}
            </p>
          </div>

          <div
            class="flex w-full flex-row items-center justify-between"
            v-show="dialogStore.content.items?.usageLimitPerCustomer"
          >
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Nombre de fois utilisé par client
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content.items?.usageLimitPerCustomer }}
            </p>
          </div>

          <div
            class="flex w-full flex-row items-center justify-between"
            v-show="dialogStore.content.items?.priority"
          >
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Priorité
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content.items?.priority }}
            </p>
          </div>
          <div class="flex w-full flex-row items-center justify-between">
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Empilable
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{
                dialogStore.content.items?.stackable
                  ? "Peut etre combiné avec d'autres promotions"
                  : "Ne peux pas etre combiné avec d'autres promotions "
              }}
            </p>
          </div>
        </div>

        <div
          class="flex w-full flex-col justify-between gap-1"
          v-show="dialogStore.content.items?.rules.length !== 0"
        >
          <div>
            <p
              class="text-grand-text text-14px font-tt-semiBold-Normal leading-normal"
            >
              Information supplementaire sur les règles
            </p>
          </div>

          <div v-for="(value, index) in dialogStore.content.items?.rules">
            <div>
              <p
                class="text-grand-text text-14px font-tt-semiBold-Normal leading-normal"
              >
                Règle numéro {{ Number(index) + 1 }}
              </p>

              <div
                class="flex w-full flex-row items-center justify-between"
                v-show="value?.type"
              >
                <p
                  class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
                >
                  Type de règle
                </p>
                <p
                  class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
                >
                  {{ value?.type }}
                </p>
              </div>

              <div
                class="flex w-full flex-row items-center justify-between"
                v-show="value?.operator"
              >
                <p
                  class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
                >
                  Type d'opérateur
                </p>
                <p
                  class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
                >
                  {{ value?.operator }}
                </p>
              </div>
              <div
                class="flex w-full flex-row items-center justify-between"
                v-show="value?.value"
              >
                <p
                  class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
                >
                  Valeur de la règle
                </p>
                <p
                  class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
                >
                  {{ value?.value }}
                </p>
              </div>

              <div class="flex w-full flex-row items-center justify-between">
                <p
                  class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
                >
                  Statut de la règle
                </p>
                <p
                  class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
                >
                  {{
                    value?.isActive
                      ? 'Cette règle est encore active'
                      : "Cette règle n'est plus active"
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          class="flex w-full flex-col justify-between gap-1"
          v-show="dialogStore.content.items?.categories.length !== 0"
        >
          <div>
            <p
              class="text-grand-text text-14px font-tt-semiBold-Normal leading-normal"
            >
              Information supplementaire sur les catégories
            </p>
          </div>

          <div v-for="value in dialogStore.content.items?.categories">
            <div class="flex w-full flex-row items-center justify-between">
              <p
                class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
              >
                Nom de la catégorie
              </p>
              <p
                class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
              >
                {{ value?.category?.designation }}
              </p>
            </div>

            <div
              class="flex w-full flex-row items-center justify-between"
              v-show="value?.category?.description"
            >
              <p
                class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
              >
                description de la catégorie
              </p>
              <p
                class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
              >
                {{ value?.category?.description }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div v-else>
        <div
          class="flex flex-row flex-wrap gap-2"
          v-if="dialogStore.content.items?.items.length !== 0"
        >
          <div v-for="item in dialogStore.content.items?.items">
            <BaseProduct
              :promotionTitle="null"
              :promotionValue="item?.discount + '%'"
              :promotionDesc="'Offert'"
              :price="item?.price"
              :categorie="item?.designation"
              :designation="item?.designation"
              :descritpion="item?.description"
              :img="item?.image?.info?.url"
              :alt="item?.image?.designation"
            />
          </div>
        </div>

        <div v-else>
          <BaseNotDataFound />
        </div>
      </div>
    </div>

    <div class="invisible h-[90px] w-full bg-amber-600" />
    <div class="fixed bottom-0 left-0 w-full bg-white py-6">
      <div class="flex flex-row items-center justify-between gap-8 px-[16px]">
        <div class="w-full" @click="handleClose">
          <BaseButtonPrimary
            type="button"
            :label="isNextStep_dialog ? 'Fermer' : 'Fermer'"
            style_of_btn="primary_color_of_btn "
            class="w-full text-white"
          />
        </div>

        <div class="w-full" @click="handleNextStep">
          <BaseButtonPrimary
            type="button"
            :label="isNextStep_dialog ? 'Précédent' : 'Voir Plus'"
            style_of_btn="secondary_color_of_btn "
            class="text-grand-text w-full"
          />
        </div>
      </div>
    </div>
  </div>
</template>
